
import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { subscribeToProfile, subscribeToSubCollection, updateUserField, addMeetingLog, addReviewLog, addServiceLog, addInventoryItem, deleteSubCollectionItem } from './services/firestoreService';
import { Layout } from './components/Layout';
import { HeroSection } from './components/HeroSection';
import { ThreeCircles } from './components/ThreeCircles';
import { NightlyReview } from './components/NightlyReview';
import { SponsorDashboard } from './components/SponsorDashboard';
import { ProfilePage } from './components/ProfilePage';
import { SponsorCheckIn } from './components/SponsorCheckIn';
import { InventoryPage } from './components/InventoryPage';
import { ServiceLedger } from './components/ServiceLedger';
import { StepsPath } from './components/StepsPath'; 
import { MeetingFinder } from './components/MeetingFinder';
import { LoginScreen } from './components/LoginScreen';
import { Book, Plus, ExternalLink, Heart, CheckCircle2, MessageSquare, HandHeart, Coffee, Calendar, BarChart3, ArrowRight, X, Sparkles, MapPin, ClipboardList, ChevronLeft, ChevronRight, Save, Loader2 } from 'lucide-react';
import { UserProfile, CircleType, MeetingEntry, Protege, ReviewLog, ServiceEntry, InventoryItem } from './types';

// Default Profile Template (Used only for types/initialization visual, not logic)
const DEFAULT_PROFILE: UserProfile = {
  id: '',
  firstName: '',
  lastInitial: '',
  email: '',
  emailVerified: false,
  phone: '',
  location: '', 
  sobrietyDates: [],
  circleItems: [],
  customQuestions: [],
  serviceHistory: [],
  meetingHistory: [],
  reviewHistory: [],
  inventory: [],
  sharingConfig: {
      shareInventory: false,
      shareReviews: false,
      shareService: true,
      shareCircles: true,
      shareSobrietyDates: true
  },
  proteges: [],
  stepProgress: []
};

// --- READ PAGE ---
interface BookPage {
    chapterTitle: string;
    chapterNumber: number;
    pageNumber: number;
    content: string;
}

const ReadPage = () => {
    const [viewMode, setViewMode] = useState<'daily' | 'full'>('daily');
    const [allPages, setAllPages] = useState<BookPage[]>([]);
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [isLoadingBook, setIsLoadingBook] = useState(false);
    
    // Daily Reflection State
    const [dailyJournalEntry, setDailyJournalEntry] = useState('');
    const [isDailySaved, setIsDailySaved] = useState(false);

    // Big Book Journal State
    const [bigBookJournalEntry, setBigBookJournalEntry] = useState('');
    const [isBigBookSaved, setIsBigBookSaved] = useState(false);

    // Load Book Content on Mount
    useEffect(() => {
        setIsLoadingBook(true);
        fetch('/big_book.json')
            .then(res => res.json())
            .then(data => {
                // Flatten the JSON structure into a linear array of pages
                const pages: BookPage[] = [];
                data.chapters.forEach((chapter: any) => {
                    chapter.pages.forEach((p: any) => {
                        pages.push({
                            chapterTitle: chapter.title,
                            chapterNumber: chapter.chapter_number,
                            pageNumber: p.page_number,
                            content: p.content
                        });
                    });
                });
                
                setAllPages(pages);
                setIsLoadingBook(false);
            })
            .catch(err => {
                console.error("Failed to load Big Book content:", err);
                setIsLoadingBook(false);
            });
    }, []);

    const handleNext = () => {
        if (currentPageIndex < allPages.length - 1) {
            setCurrentPageIndex(prev => prev + 1);
            document.getElementById('book-content')?.scrollTo(0, 0);
        }
    };

    const handlePrev = () => {
        if (currentPageIndex > 0) {
            setCurrentPageIndex(prev => prev - 1);
            document.getElementById('book-content')?.scrollTo(0, 0);
        }
    };

    const handleDailySave = () => {
        setIsDailySaved(true);
        setTimeout(() => setIsDailySaved(false), 3000);
    };

    const handleBigBookSave = () => {
        setIsBigBookSaved(true);
        setTimeout(() => setIsBigBookSaved(false), 3000);
    };

    const currentPage = allPages[currentPageIndex];

    return (
        <div className="space-y-6">
            <div className="flex gap-4 border-b border-slate-200 dark:border-slate-700 pb-2">
                 <button 
                    onClick={() => setViewMode('daily')}
                    className={`pb-2 font-medium ${viewMode === 'daily' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-slate-500'}`}
                 >
                     Daily Reflection
                 </button>
                 <button 
                    onClick={() => setViewMode('full')}
                    className={`pb-2 font-medium ${viewMode === 'full' ? 'text-brand-600 border-b-2 border-brand-600' : 'text-slate-500'}`}
                 >
                     Read Big Book
                 </button>
            </div>

            {viewMode === 'daily' ? (
                <div className="grid gap-6">
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
                        <h2 className="text-2xl font-bold dark:text-white mb-4 flex items-center gap-2">
                            <Book className="w-6 h-6 text-brand-500" /> Daily Bread
                        </h2>
                        <div className="prose dark:prose-invert max-w-none">
                            <blockquote className="border-l-4 border-brand-500 pl-4 italic text-lg text-slate-700 dark:text-slate-300">
                            "We are not cured of alcoholism. What we really have is a daily reprieve contingent on the maintenance of our spiritual condition."
                            </blockquote>
                            <p className="mt-4 text-slate-600 dark:text-slate-400 font-medium">
                            Reflection: How have I maintained my spiritual condition today? Have I prayed, meditated, or helped another?
                            </p>
                        </div>
                    </div>

                    {/* Daily Response Section */}
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold dark:text-white flex items-center gap-2">
                                <MessageSquare className="w-5 h-5 text-indigo-500" /> My Response
                            </h3>
                            {isDailySaved && (
                                <span className="text-xs font-bold text-green-600 flex items-center gap-1 animate-in fade-in">
                                    <CheckCircle2 className="w-4 h-4" /> Saved
                                </span>
                            )}
                        </div>
                        <textarea 
                            value={dailyJournalEntry}
                            onChange={(e) => setDailyJournalEntry(e.target.value)}
                            placeholder="Write your thoughts on today's reflection here..."
                            className="w-full h-32 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white resize-none focus:ring-2 focus:ring-brand-500 outline-none text-sm leading-relaxed"
                        />
                        <div className="flex justify-end mt-4">
                            <button 
                                onClick={handleDailySave}
                                disabled={!dailyJournalEntry.trim()}
                                className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg font-bold text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Save className="w-4 h-4" /> Save Entry
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid gap-6">
                    {/* BOOK READER */}
                    <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 min-h-[500px] flex flex-col relative">
                        {isLoadingBook ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                                <Loader2 className="w-8 h-8 animate-spin mb-2" />
                                <p className="text-sm">Loading Big Book...</p>
                            </div>
                        ) : currentPage ? (
                            <>
                                <div className="flex justify-between items-center mb-6 border-b border-slate-100 dark:border-slate-700 pb-4">
                                    <div>
                                        <h2 className="text-xl font-bold dark:text-white">{currentPage.chapterTitle}</h2>
                                        <span className="text-xs text-slate-400 uppercase tracking-widest">Chapter {currentPage.chapterNumber}</span>
                                    </div>
                                    <span className="px-4 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-bold rounded-lg font-mono">
                                        Page {currentPage.pageNumber}
                                    </span>
                                </div>
                                
                                <div id="book-content" className="prose dark:prose-invert max-w-none mb-8 leading-relaxed font-serif flex-1 overflow-y-auto whitespace-pre-wrap">
                                    {currentPage.content}
                                </div>
                                
                                <div className="flex justify-between items-center text-sm pt-4 border-t border-slate-100 dark:border-slate-700">
                                    <button 
                                        onClick={handlePrev}
                                        disabled={currentPageIndex === 0}
                                        className={`flex items-center gap-1 font-medium transition-colors ${
                                            currentPageIndex === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-brand-600 hover:text-brand-700'
                                        }`}
                                    >
                                        <ChevronLeft className="w-4 h-4" /> Previous Page
                                    </button>
                                    
                                    <button 
                                        onClick={handleNext}
                                        disabled={currentPageIndex === allPages.length - 1}
                                        className={`flex items-center gap-1 font-medium transition-colors ${
                                            currentPageIndex === allPages.length - 1 ? 'text-slate-300 cursor-not-allowed' : 'text-brand-600 hover:text-brand-700'
                                        }`}
                                    >
                                        Next Page <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center justify-center h-64 text-red-500">
                                Error loading content.
                            </div>
                        )}
                    </div>

                    {/* BIG BOOK JOURNAL */}
                    <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold dark:text-white flex items-center gap-2">
                                <MessageSquare className="w-5 h-5 text-indigo-500" /> Note on Page {currentPage?.pageNumber}
                            </h3>
                            {isBigBookSaved && (
                                <span className="text-xs font-bold text-green-600 flex items-center gap-1 animate-in fade-in">
                                    <CheckCircle2 className="w-4 h-4" /> Saved
                                </span>
                            )}
                        </div>
                        <textarea 
                            value={bigBookJournalEntry}
                            onChange={(e) => setBigBookJournalEntry(e.target.value)}
                            placeholder="Write what you believe the authors are saying to you today from this reading."
                            className="w-full h-32 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white resize-none focus:ring-2 focus:ring-brand-500 outline-none text-sm leading-relaxed"
                        />
                        <div className="flex justify-end mt-4">
                            <button 
                                onClick={handleBigBookSave}
                                disabled={!bigBookJournalEntry.trim()}
                                className="bg-brand-600 hover:bg-brand-700 text-white px-5 py-2 rounded-lg font-bold text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <Save className="w-4 h-4" /> Save Note
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- MEETING TRACKER MODAL ---
const LogMeetingModal = ({ isOpen, onClose, onSave }: any) => {
    const [type, setType] = useState('AA');
    const [name, setName] = useState('');
    const [online, setOnline] = useState(false);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
            <div className="relative w-full max-w-sm bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 animate-in zoom-in duration-200">
                <h3 className="text-lg font-bold dark:text-white mb-4">Log a Meeting</h3>
                <div className="space-y-3">
                    <select value={type} onChange={e => setType(e.target.value)} className="w-full p-3 rounded-lg border dark:bg-slate-900 dark:text-white dark:border-slate-700">
                        <option>AA</option><option>NA</option><option>CA</option><option>HA</option><option>Other</option>
                    </select>
                    <input 
                        placeholder="Meeting Name (Optional)" 
                        value={name} onChange={e => setName(e.target.value)}
                        className="w-full p-3 rounded-lg border dark:bg-slate-900 dark:text-white dark:border-slate-700"
                    />
                    <div className="flex items-center gap-2">
                        <input type="checkbox" checked={online} onChange={e => setOnline(e.target.checked)} id="online" />
                        <label htmlFor="online" className="text-slate-700 dark:text-slate-300">Online Meeting?</label>
                    </div>
                    <button 
                        onClick={() => { onSave({ type, name, online }); onClose(); }}
                        className="w-full bg-brand-600 text-white py-3 rounded-lg font-bold"
                    >Save</button>
                </div>
            </div>
        </div>
    )
}

// --- PROGRESS CENTER WIDGET ---
const ProgressCenter = ({ profile, onViewAll, onLogMeeting, onLogReview }: { profile: UserProfile, onViewAll: () => void, onLogMeeting: () => void, onLogReview: () => void }) => {
    const [activeTab, setActiveTab] = useState<'meetings' | 'review'>('meetings');
    
    // Meeting Data: Last 7 days
    const getMeetingChartData = () => {
        const counts = Array(7).fill(0);
        const today = new Date();
        profile.meetingHistory?.forEach(m => {
            const mDate = new Date(m.date);
            const diffTime = Math.abs(today.getTime() - mDate.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays <= 7 && diffDays > 0) {
                counts[7 - diffDays]++; 
            } else if (diffDays === 0) {
                counts[6]++; // Today
            }
        });
        return counts; 
    };

    const meetingData = getMeetingChartData();
    const totalMeetings = profile.meetingHistory?.length || 0;

    // Review Data: Last 7 reviews
    const recentReviews = profile.reviewHistory?.slice(0, 7).reverse() || [];
    const totalReviews = profile.reviewHistory?.length || 0;

    return (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 min-h-[300px] flex flex-col relative overflow-hidden">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold dark:text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-brand-500" /> Recovery Progress
                </h3>
                <button 
                    onClick={onViewAll}
                    className="text-xs text-brand-600 dark:text-brand-400 hover:underline font-medium flex items-center"
                >
                    See all charts <ArrowRight className="w-3 h-3 ml-0.5" />
                </button>
            </div>
            
            <div className="flex mb-6 bg-slate-100 dark:bg-slate-700/50 p-1 rounded-lg">
                <button 
                    onClick={() => setActiveTab('meetings')}
                    className={`flex-1 text-xs font-bold py-1.5 rounded-md transition-all ${activeTab === 'meetings' ? 'bg-white dark:bg-slate-600 shadow-sm text-brand-600 dark:text-brand-300' : 'text-slate-400'}`}
                >
                    Meetings
                </button>
                <button 
                    onClick={() => setActiveTab('review')}
                    className={`flex-1 text-xs font-bold py-1.5 rounded-md transition-all ${activeTab === 'review' ? 'bg-white dark:bg-slate-600 shadow-sm text-brand-600 dark:text-brand-300' : 'text-slate-400'}`}
                >
                    Spirituality
                </button>
            </div>

            <div className="flex-1 flex flex-col justify-end">
                {activeTab === 'meetings' ? (
                    totalMeetings === 0 ? (
                        <div className="flex flex-col items-center justify-center text-center h-full py-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-100 dark:border-slate-700 border-dashed">
                             <Coffee className="w-8 h-8 text-slate-300 mb-2" />
                             <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-3">No meetings logged yet.</p>
                             <button 
                                onClick={onLogMeeting}
                                className="text-xs bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg font-bold"
                             >
                                 Log a meeting to start tracking progress
                             </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-between items-end mb-4">
                                <div>
                                    <span className="text-3xl font-bold dark:text-white block">{totalMeetings}</span>
                                    <span className="text-xs text-slate-500">Total Meetings Logged</span>
                                </div>
                            </div>
                            {/* CHART AREA */}
                            <div className="h-32 flex items-end gap-2 border-b border-slate-200 dark:border-slate-600 pb-1">
                                {meetingData.map((count, i) => (
                                    <div key={i} className="flex-1 flex flex-col justify-end h-full group relative bg-slate-100 dark:bg-slate-700 rounded-t-sm">
                                        <div 
                                            className={`w-full rounded-t-sm transition-all duration-500 ${count > 0 ? 'bg-amber-400 dark:bg-amber-500' : 'bg-transparent'}`} 
                                            style={{ height: `${Math.max(count * 20, 0)}%` }} 
                                        ></div>
                                        {/* Tooltip */}
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                            {count} Meetings
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-between mt-2 text-[10px] text-slate-400 uppercase tracking-wider font-medium">
                                <span>7 Days Ago</span>
                                <span>Today</span>
                            </div>
                        </>
                    )
                ) : (
                    totalReviews === 0 ? (
                        <div className="flex flex-col items-center justify-center text-center h-full py-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-100 dark:border-slate-700 border-dashed">
                             <ClipboardList className="w-8 h-8 text-slate-300 mb-2" />
                             <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-3">No nightly reviews found.</p>
                             <button 
                                onClick={onLogReview}
                                className="text-xs bg-brand-600 hover:bg-brand-700 text-white px-3 py-1.5 rounded-lg font-bold"
                             >
                                 Enter a nightly review to start tracking progress
                             </button>
                        </div>
                    ) : (
                        <>
                             <div className="flex justify-between items-end mb-4">
                                 <div>
                                     <span className="text-3xl font-bold dark:text-white block">
                                         {recentReviews.length > 0 
                                            ? (recentReviews.reduce((a,b) => a + (b.aiResponse.sentimentScore || 0), 0) / recentReviews.length).toFixed(1)
                                            : '-'}
                                     </span>
                                     <span className="text-xs text-slate-500">Avg. Spiritual Condition (1-10)</span>
                                 </div>
                            </div>
                            {/* CHART AREA */}
                            <div className="h-32 flex items-end gap-2 border-b border-slate-200 dark:border-slate-600 pb-1">
                                {recentReviews.length === 0 ? (
                                    <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">No recent reviews</div>
                                ) : (
                                    [...Array(7)].map((_, i) => {
                                        const paddedReviews = Array(7).fill(null).map((_, idx) => {
                                             const offset = 7 - recentReviews.length;
                                             if (idx < offset) return null;
                                             return recentReviews[idx - offset];
                                        });

                                        const entry = paddedReviews[i];
                                        
                                        if (!entry) return <div key={i} className="flex-1 h-full bg-slate-100 dark:bg-slate-700 rounded-t-sm"></div>;
                                        
                                        const score = entry.aiResponse.sentimentScore || 5;
                                        return (
                                            <div key={i} className="flex-1 flex flex-col justify-end h-full group relative bg-slate-100 dark:bg-slate-700 rounded-t-sm">
                                                <div 
                                                    className={`w-full rounded-t-sm transition-all duration-500 ${
                                                        score >= 8 ? 'bg-green-400 dark:bg-green-500' : score >= 5 ? 'bg-amber-400 dark:bg-amber-500' : 'bg-red-400 dark:bg-red-500'
                                                    }`}
                                                    style={{ height: `${score * 10}%` }}
                                                ></div>
                                                 <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                                    Score: {score}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                            <div className="flex justify-between mt-2 text-[10px] text-slate-400 uppercase tracking-wider font-medium">
                                <span>Past</span>
                                <span>Recent</span>
                            </div>
                        </>
                    )
                )}
            </div>
        </div>
    );
};

// --- DASHBOARD (THE TRUE HUB) ---
interface DashboardProps {
    profile: UserProfile;
    setProfile: (p: UserProfile) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ profile, setProfile }) => {
    const [isCheckInOpen, setIsCheckInOpen] = useState(false);
    const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
    
    // Review Modal Logic
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [reviewInitialTab, setReviewInitialTab] = useState<'checkin' | 'history' | 'trends'>('checkin');

    // Meeting Finder Modal Logic
    const [isFinderOpen, setIsFinderOpen] = useState(false);

    // Gratitude State
    const [gratitudeItems, setGratitudeItems] = useState<string[]>(['', '', '']);
    const [completedCount, setCompletedCount] = useState(0);
    
    const { user } = useAuth();

    const handleMeetingSave = async (entry: Omit<MeetingEntry, 'id'>) => {
        if (!user) return;
        await addMeetingLog(user.uid, entry);
        setIsMeetingModalOpen(false);
    };

    const openReviewModal = (tab: 'checkin' | 'history' | 'trends' = 'checkin') => {
        setReviewInitialTab(tab);
        setIsReviewModalOpen(true);
    }

    return (
        <div className="space-y-6">
            {/* Header always at top */}
            <HeroSection 
                cleanDate={profile.sobrietyDates[0]?.date || new Date().toISOString()} 
                onCheckInClick={() => setIsCheckInOpen(true)}
            />
            
            <SponsorCheckIn isOpen={isCheckInOpen} onClose={() => setIsCheckInOpen(false)} />
            <LogMeetingModal isOpen={isMeetingModalOpen} onClose={() => setIsMeetingModalOpen(false)} onSave={handleMeetingSave} />
            
            {/* Modal for Full Review Entry */}
            {isReviewModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsReviewModalOpen(false)}></div>
                    <div className="relative w-full max-w-2xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col max-h-[85vh] animate-in slide-in-from-bottom-4 overflow-hidden">
                        <div className="sticky top-0 bg-white dark:bg-slate-800 p-4 border-b dark:border-slate-700 flex justify-between items-center z-10 flex-shrink-0">
                            <h3 className="font-bold dark:text-white">Nightly Review</h3>
                            <button onClick={() => setIsReviewModalOpen(false)}><X className="w-6 h-6 text-slate-400" /></button>
                        </div>
                        <div className="p-4 overflow-y-auto">
                            <NightlyReview profile={profile} onUpdate={setProfile} initialTab={reviewInitialTab} />
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for Meeting Finder ("The Bridge") */}
            {isFinderOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsFinderOpen(false)}></div>
                    <div className="relative w-full max-w-4xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in duration-200 overflow-hidden">
                        <div className="sticky top-0 bg-white dark:bg-slate-800 p-4 border-b dark:border-slate-700 flex justify-between items-center z-10 flex-shrink-0">
                            <h3 className="font-bold dark:text-white">Find a Meeting</h3>
                            <button onClick={() => setIsFinderOpen(false)}><X className="w-6 h-6 text-slate-400" /></button>
                        </div>
                        <div className="p-6 overflow-y-auto bg-slate-50 dark:bg-slate-900">
                            <MeetingFinder userLocation={profile.location} />
                        </div>
                    </div>
                </div>
            )}

            {/* MAIN LAYOUT CONTAINER 
                Mobile: Flex Column (for specific ordering)
                Desktop: Grid 2 Columns
            */}
            <div className="flex flex-col md:grid md:grid-cols-2 gap-6">
                
                {/* 
                   DESKTOP LEFT COLUMN CONTENT 
                   Wrapped in contents for mobile order utility to work on children
                */}
                <div className="contents md:block space-y-6">
                    {/* 1. ACTION BUTTONS (Mobile Order 1) */}
                    <div className="order-1 md:mb-6 grid grid-cols-2 gap-3">
                        <button 
                            onClick={() => setIsMeetingModalOpen(true)}
                            className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center gap-2 hover:border-brand-500 transition-colors"
                        >
                            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center text-amber-600 dark:text-amber-400">
                                <Coffee className="w-5 h-5" />
                            </div>
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 text-center">Log Meeting</span>
                        </button>

                         <button 
                            onClick={() => window.location.hash = '#/service'}
                            className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center gap-2 hover:border-brand-500 transition-colors"
                        >
                            <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                                <HandHeart className="w-5 h-5" />
                            </div>
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 text-center">Log Service</span>
                        </button>

                        <button 
                            onClick={() => setIsFinderOpen(true)}
                            className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center gap-2 hover:border-brand-500 transition-colors"
                        >
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400">
                                <MapPin className="w-5 h-5" />
                            </div>
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 text-center">Find Meeting</span>
                        </button>

                        <button 
                            onClick={() => openReviewModal('checkin')}
                            className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center gap-2 hover:border-brand-500 transition-colors"
                        >
                            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                <MessageSquare className="w-5 h-5" />
                            </div>
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300 text-center">Nightly Review</span>
                        </button>
                    </div>

                    {/* 2. GRATITUDE (Mobile Order 2) */}
                    <div className="order-2 md:mb-6 bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                        <h3 className="font-bold dark:text-white mb-4 flex items-center gap-2">
                            <Heart className="w-5 h-5 text-indigo-500" /> Daily Gratitude
                        </h3>
                        {completedCount === 3 ? (
                            <div className="text-center py-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                <p className="text-green-700 dark:text-green-300 font-bold flex items-center justify-center gap-2">
                                    <CheckCircle2 className="w-5 h-5" /> Logged!
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {gratitudeItems.map((item, idx) => (
                                    <input 
                                        key={idx}
                                        value={item}
                                        onChange={e => {
                                            const n = [...gratitudeItems]; n[idx] = e.target.value; setGratitudeItems(n);
                                        }}
                                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-3 text-sm focus:ring-1 focus:ring-brand-500 outline-none"
                                        placeholder={`I am grateful for...`}
                                    />
                                ))}
                                <button 
                                    onClick={() => setCompletedCount(3)}
                                    className="w-full mt-2 bg-brand-600 hover:bg-brand-700 text-white py-3 rounded-lg font-bold text-sm shadow-md transition-all"
                                >Save Entries</button>
                            </div>
                        )}
                    </div>

                    {/* 4. PROGRESS (Mobile Order 4) */}
                    <div className="order-4 md:mb-0">
                        <ProgressCenter 
                            profile={profile} 
                            onViewAll={() => openReviewModal('trends')} 
                            onLogMeeting={() => setIsMeetingModalOpen(true)}
                            onLogReview={() => openReviewModal('checkin')}
                        />
                    </div>
                </div>

                {/* 
                   DESKTOP RIGHT COLUMN CONTENT
                   Wrapped in contents for mobile order utility
                */}
                <div className="contents md:block space-y-6">
                    {/* 3. STEPS JOURNEY (Mobile Order 3) */}
                    <div className="order-3 md:mb-6">
                        <StepsPath profile={profile} onUpdate={setProfile} />
                    </div>

                    {/* 5. THREE CIRCLES (Mobile Order 5) */}
                    <div className="order-5 md:mb-0">
                        <ThreeCircles items={profile.circleItems} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default function App() {
  const { user, loading } = useAuth();
  const [profile, setProfile] = useState<UserProfile>(DEFAULT_PROFILE);
  const [isDark, setIsDark] = useState(false);
  
  // Theme Toggle Effect
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
      document.documentElement.classList.add('dark');
      setIsDark(true);
    } else {
      document.documentElement.classList.remove('dark');
      setIsDark(false);
    }
  }, []);

  // Data Fetching Effect (The Scalable Architecture Wiring)
  useEffect(() => {
      if (!user) return;

      const unsubs: (() => void)[] = [];

      // 1. Fetch Core Profile
      unsubs.push(subscribeToProfile(user.uid, (data) => {
          setProfile(prev => ({ ...prev, ...data, id: user.uid, email: user.email || '' }));
      }));

      // 2. Fetch Meetings (Sub-collection)
      unsubs.push(subscribeToSubCollection(user.uid, 'meetings', (data) => {
          setProfile(prev => ({ ...prev, meetingHistory: data as MeetingEntry[] }));
      }));

      // 3. Fetch Reviews (Sub-collection)
      unsubs.push(subscribeToSubCollection(user.uid, 'reviews', (data) => {
          setProfile(prev => ({ ...prev, reviewHistory: data as ReviewLog[] }));
      }));

      // 4. Fetch Service (Sub-collection)
      unsubs.push(subscribeToSubCollection(user.uid, 'service', (data) => {
          setProfile(prev => ({ ...prev, serviceHistory: data as ServiceEntry[] }));
      }));

      // 5. Fetch Inventory (Sub-collection)
      unsubs.push(subscribeToSubCollection(user.uid, 'inventory', (data) => {
          setProfile(prev => ({ ...prev, inventory: data as InventoryItem[] }));
      }));

      return () => {
          unsubs.forEach(unsub => unsub());
      };
  }, [user]);

  const toggleTheme = () => {
    if (isDark) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
      setIsDark(false);
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setIsDark(true);
    }
  };

  // Wrapper for updates to handle local optimistic UI + Firestore Write
  const handleProfileUpdate = async (updatedProfile: UserProfile) => {
    // 1. Optimistic Update
    setProfile(updatedProfile);
    
    // 2. Persist Core Fields to Firestore
    if (user) {
        // We filter out the history arrays because they are managed via sub-collections now
        const { meetingHistory, reviewHistory, serviceHistory, inventory, ...coreData } = updatedProfile;
        await updateUserField(user.uid, 'settings', coreData); // Logic simplified: updateUserField handles fields. 
        // Note: For a real app, we'd map individual fields, but for this demo, we assume firestoreService handles object merging.
        
        // Specifically saving complex objects that live on the main profile
        updateUserField(user.uid, 'sobrietyDates', updatedProfile.sobrietyDates);
        updateUserField(user.uid, 'circleItems', updatedProfile.circleItems);
        updateUserField(user.uid, 'customQuestions', updatedProfile.customQuestions);
        updateUserField(user.uid, 'sharingConfig', updatedProfile.sharingConfig);
        updateUserField(user.uid, 'stepProgress', updatedProfile.stepProgress);
        updateUserField(user.uid, 'proteges', updatedProfile.proteges);
        updateUserField(user.uid, 'firstName', updatedProfile.firstName);
        updateUserField(user.uid, 'lastInitial', updatedProfile.lastInitial);
        updateUserField(user.uid, 'phone', updatedProfile.phone);
        updateUserField(user.uid, 'location', updatedProfile.location);
    }
  };

  const handleUpdateProtege = (updatedProtege: Protege) => {
     const updatedList = profile.proteges.map(p => p.id === updatedProtege.id ? updatedProtege : p);
     handleProfileUpdate({ ...profile, proteges: updatedList });
  }

  // Inventory Handlers
  const handleAddInventory = async (item: Omit<InventoryItem, 'id'>) => {
      if (!user) return;
      await addInventoryItem(user.uid, item);
  }

  const handleDeleteInventory = async (id: string) => {
      if (!user) return;
      await deleteSubCollectionItem(user.uid, 'inventory', id);
  }

  if (loading) {
      return (
          <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
              <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
          </div>
      )
  }

  if (!user) {
      return <LoginScreen />;
  }

  return (
    <HashRouter>
      <Layout userProfile={profile} isDark={isDark} toggleTheme={toggleTheme}>
        <Routes>
          <Route path="/" element={<Dashboard profile={profile} setProfile={handleProfileUpdate} />} />
          <Route 
             path="/inventory" 
             element={<InventoryPage inventory={profile.inventory} onAdd={handleAddInventory} onDelete={handleDeleteInventory} />} 
          />
          <Route path="/service" element={<ServiceLedger profile={profile} onUpdate={handleProfileUpdate} />} />
          <Route 
             path="/sponsor" 
             element={<SponsorDashboard proteges={profile.proteges} onUpdateProtege={handleUpdateProtege} />} 
          />
          <Route path="/read" element={<ReadPage />} />
          <Route 
            path="/profile" 
            element={
                <ProfilePage 
                    profile={profile} 
                    onUpdate={handleProfileUpdate} 
                    isDark={isDark} 
                    toggleTheme={toggleTheme} 
                />
            } 
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Layout>
    </HashRouter>
  );
}
